/**
 * Developer admin panel component.
 * Only visible in development environment.
 * Displays all developer-visible settings (both DEVELOPER and DEVELOPER_HIDDEN).
 *
 * This is an example implementation. You can customize the UI to match your needs.
 */

import { useState, useEffect } from 'react';
import {
  useDevSettingsStore,
  isDevSettingsAvailable,
  type CameraSettingBounds,
} from '@shared/stores/devSettingsStore';
import { useGameStateStore } from '@shared/stores';
import { getSettingsByCategory, ConfigCategory, ConfigVisibility } from '@shared/config/utils';
import { CAMERA_CONFIG_SCHEMA } from '@shared/config/settings/camera';
import { BUILDING_CONFIG_SCHEMA, DEFAULT_BUILDING_CONFIG } from '@shared/config/settings/buildings';
import { UNIT_CONFIG_SCHEMA, getDefaultUnitConfig } from '@shared/config/settings/units';
import { UnitType, UNIT_DEFINITIONS } from '@shared/constants/units';
import type { ConfigMetadata } from '@shared/config/types';

/**
 * Component for rendering a numeric setting with both slider and text input.
 */
function NumericSettingInput({
  value,
  metadata,
  onChange,
}: {
  value: number;
  metadata: ConfigMetadata;
  onChange: (value: number) => void;
}) {
  const [localValue, setLocalValue] = useState<string>(value.toString());

  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  return (
    <div className="flex items-center gap-4">
      <input
        type="range"
        min={metadata.min}
        max={metadata.max}
        step={metadata.step}
        value={value}
        onChange={(e) => {
          const val = parseFloat(e.target.value);
          onChange(val);
          setLocalValue(val.toString());
        }}
        className="flex-1"
      />
      <input
        type="number"
        min={metadata.min}
        max={metadata.max}
        step={metadata.step}
        value={localValue}
        onChange={(e) => {
          const val = e.target.value;
          setLocalValue(val);
          const numVal = parseFloat(val);
          if (!isNaN(numVal)) {
            const clamped = Math.max(metadata.min || 0, Math.min(metadata.max || Infinity, numVal));
            onChange(clamped);
            setLocalValue(clamped.toString());
          }
        }}
        onBlur={(e) => {
          const numVal = parseFloat(e.target.value);
          if (isNaN(numVal)) {
            setLocalValue(value.toString());
          }
        }}
        className="text-white font-mono bg-gray-700 border border-gray-600 rounded px-2 py-1 w-24 text-right"
      />
    </div>
  );
}

/**
 * Component for rendering a color setting with color picker and hex input.
 * Converts between numeric hex (0x333333) and string hex (#333333).
 */
function ColorInput({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  // Convert numeric hex (0x333333) to string hex (#333333)
  const numToHex = (num: number): string => {
    return '#' + num.toString(16).padStart(6, '0').toUpperCase();
  };

  // Convert string hex (#333333) to numeric hex (0x333333)
  const hexToNum = (hex: string): number => {
    const cleaned = hex.replace('#', '').trim();
    if (cleaned.length !== 6) return value; // Invalid, keep current value
    const parsed = parseInt(cleaned, 16);
    if (isNaN(parsed)) return value;
    return parsed;
  };

  const [hexValue, setHexValue] = useState<string>(numToHex(value));

  // Sync hex value when external value changes
  useEffect(() => {
    setHexValue(numToHex(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHex = e.target.value.toUpperCase();
    setHexValue(newHex);
    onChange(hexToNum(newHex));
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
      .toUpperCase()
      .replace(/[^0-9A-F]/g, '')
      .slice(0, 6);
    setHexValue('#' + inputValue);
    // Only update if we have 6 hex digits
    if (inputValue.length === 6) {
      onChange(hexToNum('#' + inputValue));
    }
  };

  const handleHexInputBlur = () => {
    // Validate and fix hex on blur - pad with zeros if needed, or restore if invalid
    const hexWithoutHash = hexValue.replace('#', '');
    if (hexWithoutHash.length === 6 && /^[0-9A-F]{6}$/i.test(hexWithoutHash)) {
      const normalized = hexWithoutHash.toUpperCase();
      setHexValue('#' + normalized);
      onChange(hexToNum('#' + normalized));
    } else {
      // Restore to current value
      setHexValue(numToHex(value));
    }
  };

  return (
    <div className="flex items-center gap-4">
      <input
        type="color"
        value={hexValue}
        onChange={handleColorPickerChange}
        className="w-16 h-10 cursor-pointer rounded border border-gray-600"
      />
      <div className="flex items-center gap-2 flex-1">
        <span className="text-gray-400 text-sm">#</span>
        <input
          type="text"
          value={hexValue.replace('#', '')}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
          placeholder="333333"
          maxLength={6}
          className="text-white font-mono bg-gray-700 border border-gray-600 rounded px-2 py-1 flex-1 uppercase"
          pattern="[0-9A-Fa-f]{6}"
        />
      </div>
    </div>
  );
}

export default function DevAdminPanel() {
  const {
    settings,
    updateCameraSettings,
    updateCameraBounds,
    updateBuildingSettings,
    updateUnitSettings,
    updateMapSelection,
    resetCameraSettings,
    resetCameraBounds,
    resetBuildingSettings,
    resetUnitSettings,
  } = useDevSettingsStore();
  const { returnToMenu } = useGameStateStore();
  const [activeCategory, setActiveCategory] = useState<ConfigCategory>(ConfigCategory.CAMERA);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Starting Stats': false,
    'Building Costs': false,
    'Construction Times': false,
    'Health & Armor': false,
    'Repair Settings': false,
    'Mining Income': false,
    'Upgrade Settings': false,
    'Progress Bar': false,
    'Health Bar': false,
    'Builder Stats': false,
    'Melee Stats': false,
    'Ranged Stats': false,
    'Enemy Melee Stats': false,
    'Spawn Points': false,
  });

  // Don't render in production
  if (!isDevSettingsAvailable()) {
    return null;
  }

  // Get all developer settings (both visible and hidden)
  const allDevSettings = getSettingsByCategory([
    ConfigVisibility.DEVELOPER,
    ConfigVisibility.DEVELOPER_HIDDEN,
  ]);

  const handleCameraSettingChange = (key: string, value: number | boolean) => {
    updateCameraSettings({ [key]: value });
  };

  const handleBoundsChange = (
    settingKey: keyof CameraSettingBounds,
    boundType: 'min' | 'max',
    value: number
  ) => {
    const currentBounds = settings.cameraBounds ?? {
      panSpeed: { min: 1, max: 50 },
      edgeScrollSpeed: { min: 1, max: 50 },
    };
    updateCameraBounds({
      [settingKey]: {
        ...currentBounds[settingKey],
        [boundType]: value,
      },
    });
  };

  const handleBuildingSettingChange = (key: string, value: number | boolean) => {
    updateBuildingSettings({ [key]: value });
  };

  const handleUnitSettingChange = (key: string, value: number | boolean) => {
    updateUnitSettings({ [key]: value });
  };

  // Filter out empty categories (like DEBUG if it has no settings)
  const categories = (Object.keys(allDevSettings) as ConfigCategory[]).filter(
    (category) => {
      const categorySettings = allDevSettings[category];
      return categorySettings && categorySettings.length > 0;
    }
  );
  // Safety fallback for bounds (store hydration may be delayed)
  const cameraBounds = settings.cameraBounds ?? {
    panSpeed: { min: 1, max: 50 },
    edgeScrollSpeed: { min: 1, max: 50 },
  };
  // Safety fallback for building settings (older persisted dev-settings may not have this key yet)
  const buildingSettings = settings.buildings ?? DEFAULT_BUILDING_CONFIG;
  // Safety fallback for unit settings (older persisted dev-settings may not have this key yet)
  const unitSettings = settings.units ?? getDefaultUnitConfig();

  return (
    <div className="w-screen h-screen bg-gray-900 flex flex-col overflow-hidden">
      <div className="w-full max-w-4xl mx-auto px-8 pt-8 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-yellow-400">Developer Admin Panel</h2>
          <button
            onClick={returnToMenu}
            className="px-6 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors"
          >
            Back to Menu
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded transition-colors ${
                activeCategory === category
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="w-full max-w-4xl mx-auto px-8 pb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            {activeCategory === ConfigCategory.CAMERA && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Camera Settings (Developer)
                </h3>

                {/* Dev-only settings (including min/max zoom) */}
                {Object.entries(CAMERA_CONFIG_SCHEMA)
                  .filter(
                    ([_, metadata]) =>
                      (metadata.visibility === ConfigVisibility.DEVELOPER ||
                        metadata.visibility === ConfigVisibility.DEVELOPER_HIDDEN) &&
                      metadata.category === ConfigCategory.CAMERA
                  )
                  .map(([key, metadata]) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-gray-200">
                        <span className="font-medium">{metadata.label}</span>
                        {metadata.description && (
                          <span className="block text-sm text-gray-400 mt-1">
                            {metadata.description}
                          </span>
                        )}
                      </label>
                      {(() => {
                        const settingValue = settings.camera[key as keyof typeof settings.camera];
                        if (typeof settingValue === 'boolean') {
                          return (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={settingValue}
                                onChange={(e) => handleCameraSettingChange(key, e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-gray-300">
                                {settingValue ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                          );
                        } else {
                          const numValue = settingValue as number;
                          return (
                            <div className="flex items-center gap-4">
                              <input
                                type="range"
                                min={metadata.min}
                                max={metadata.max}
                                step={metadata.step}
                                value={numValue || 0}
                                onChange={(e) =>
                                  handleCameraSettingChange(key, parseFloat(e.target.value))
                                }
                                className="flex-1"
                              />
                              <span className="text-white font-mono w-16 text-right">
                                {numValue || 0}
                              </span>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  ))}

                {/* Player Speed Bounds */}
                <div className="pt-6 border-t border-gray-700">
                  <h4 className="text-lg font-semibold text-yellow-300 mb-4">
                    Player Speed Bounds
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Set the min/max bounds that constrain what players can adjust for pan and edge
                    scroll speeds.
                  </p>

                  <div className="mb-6 space-y-3">
                    <label className="block text-gray-200 font-medium">Pan Speed Bounds</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Min Pan Speed</label>
                        <input
                          type="range"
                          min={1}
                          max={cameraBounds.panSpeed.max}
                          step={1}
                          value={cameraBounds.panSpeed.min}
                          onChange={(e) =>
                            handleBoundsChange('panSpeed', 'min', parseFloat(e.target.value) || 0)
                          }
                          className="w-full"
                        />
                        <span className="text-white font-mono">{cameraBounds.panSpeed.min}</span>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Max Pan Speed</label>
                        <input
                          type="range"
                          min={cameraBounds.panSpeed.min}
                          max={50}
                          step={1}
                          value={cameraBounds.panSpeed.max}
                          onChange={(e) =>
                            handleBoundsChange('panSpeed', 'max', parseFloat(e.target.value) || 0)
                          }
                          className="w-full"
                        />
                        <span className="text-white font-mono">{cameraBounds.panSpeed.max}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 space-y-3">
                    <label className="block text-gray-200 font-medium">
                      Edge Scroll Speed Bounds
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">
                          Min Edge Scroll Speed
                        </label>
                        <input
                          type="range"
                          min={1}
                          max={cameraBounds.edgeScrollSpeed.max}
                          step={1}
                          value={cameraBounds.edgeScrollSpeed.min}
                          onChange={(e) =>
                            handleBoundsChange(
                              'edgeScrollSpeed',
                              'min',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full"
                        />
                        <span className="text-white font-mono">
                          {cameraBounds.edgeScrollSpeed.min}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">
                          Max Edge Scroll Speed
                        </label>
                        <input
                          type="range"
                          min={cameraBounds.edgeScrollSpeed.min}
                          max={50}
                          step={1}
                          value={cameraBounds.edgeScrollSpeed.max}
                          onChange={(e) =>
                            handleBoundsChange(
                              'edgeScrollSpeed',
                              'max',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full"
                        />
                        <span className="text-white font-mono">
                          {cameraBounds.edgeScrollSpeed.max}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={resetCameraBounds}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                  >
                    Reset Bounds to Defaults
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={resetCameraSettings}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reset Camera Settings
                  </button>
                </div>
              </div>
            )}

            {activeCategory === ConfigCategory.DESIGN && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">Buildings (Design)</h3>

                {/* Define groups for design settings */}
                {(() => {
                  const designGroups = [
                    {
                      title: 'Progress Bar',
                      keys: [
                        'progressBarBackgroundColor',
                        'progressBarFillColor',
                        'progressBarBorderColor',
                        'progressBarPausedOverlayColor',
                        'progressBarBackgroundAlpha',
                        'progressBarBorderAlpha',
                        'progressBarPausedOverlayAlpha',
                      ] as const,
                    },
                    {
                      title: 'Health Bar',
                      keys: [
                        'healthBarBackgroundColor',
                        'healthBarHighColor',
                        'healthBarMidColor',
                        'healthBarLowColor',
                        'healthBarBorderColor',
                        'healthBarBackgroundAlpha',
                        'healthBarBorderAlpha',
                      ] as const,
                    },
                  ];

                  return designGroups.map((group, groupIndex) => {
                    const validKeys = group.keys.filter(
                      (key) =>
                        BUILDING_CONFIG_SCHEMA[key] &&
                        BUILDING_CONFIG_SCHEMA[key].category === ConfigCategory.DESIGN
                    );

                    if (validKeys.length === 0) return null;

                    const isExpanded = expandedSections[group.title] ?? false;

                    return (
                      <div
                        key={group.title}
                        className={`space-y-3 ${groupIndex > 0 ? 'pt-4 border-t border-gray-700' : ''}`}
                      >
                        <button
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [group.title]: !isExpanded,
                            }))
                          }
                          className="flex items-center gap-2 w-full text-left hover:opacity-80 transition-opacity"
                        >
                          <span
                            className={`text-yellow-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          >
                            ▶
                          </span>
                          <h4 className="text-lg font-semibold text-yellow-300">{group.title}</h4>
                        </button>
                        {isExpanded && (
                          <div className="space-y-3 pl-6">
                            {validKeys.map((key) => {
                              const metadata = BUILDING_CONFIG_SCHEMA[key];
                              if (!metadata) return null;

                              const settingValue = buildingSettings[key];
                              const numValue =
                                (settingValue as number | undefined) ??
                                DEFAULT_BUILDING_CONFIG[key];

                              const isColor = key.endsWith('Color');

                              return (
                                <div key={key} className="space-y-2">
                                  <label className="block text-gray-200">
                                    <span className="font-medium">{metadata.label}</span>
                                    {metadata.description && (
                                      <span className="block text-sm text-gray-400 mt-1">
                                        {metadata.description}
                                      </span>
                                    )}
                                  </label>
                                  {isColor ? (
                                    <ColorInput
                                      value={numValue}
                                      onChange={(val) => handleBuildingSettingChange(key, val)}
                                    />
                                  ) : (
                                    <NumericSettingInput
                                      value={numValue}
                                      metadata={metadata}
                                      onChange={(val) => handleBuildingSettingChange(key, val)}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={resetBuildingSettings}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reset Building Design Settings
                  </button>
                </div>
              </div>
            )}

            {activeCategory === ConfigCategory.GAMEPLAY && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">Map Selection</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-200 font-medium mb-2">
                      Select Map to Load
                    </label>
                    <p className="text-sm text-gray-400 mb-3">
                      Choose which map to load when starting a new game. Changes take effect on next game start.
                    </p>
                    <select
                      value={settings.mapSelection ?? 'procedural'}
                      onChange={(e) => updateMapSelection(e.target.value as 'static-map' | 'test-map' | 'procedural')}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    >
                      <option value="procedural">Procedural (Generated)</option>
                      <option value="static-map">Static Map (static-map.json)</option>
                      <option value="test-map">Test Map (test-map.json)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">
                      Current selection: <span className="text-yellow-400">{settings.mapSelection ?? 'procedural'}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeCategory === ConfigCategory.BUILDINGS && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Building Settings (Developer)
                </h3>

                {/* Define groups for building settings */}
                {(() => {
                  const groups = [
                    {
                      title: 'Starting Stats',
                      keys: ['startingCurrency', 'startingBuilderCount'] as const,
                    },
                    {
                      title: 'Building Costs',
                      keys: [
                        'towerArcherCost',
                        'towerCannonCost',
                        'towerMagicCost',
                        'barracksCost',
                        'heroSpawnerCost',
                        'techBuildingCost',
                        'mineCost',
                      ] as const,
                    },
                    {
                      title: 'Construction Times',
                      keys: [
                        'towerConstructionTime',
                        'mineConstructionTime',
                        'barracksConstructionTime',
                        'heroSpawnerConstructionTime',
                        'techBuildingConstructionTime',
                      ] as const,
                    },
                    {
                      title: 'Health & Armor',
                      keys: [
                        'towerHealth',
                        'mineHealth',
                        'barracksHealth',
                        'heroSpawnerHealth',
                        'techBuildingHealth',
                        'wallHealth',
                        'towerArmor',
                        'mineArmor',
                        'barracksArmor',
                        'heroSpawnerArmor',
                        'techBuildingArmor',
                        'wallArmor',
                      ] as const,
                    },
                    {
                      title: 'Repair Settings',
                      keys: [
                        'repairProximityRange',
                        'repairRate',
                        'repairCostPerTick',
                        'repairTickInterval',
                      ] as const,
                    },
                    {
                      title: 'Mining Income',
                      keys: [
                        'miningBaseIncome',
                        'miningIncomePerLevel',
                        'miningIncomeInterval',
                      ] as const,
                    },
                    {
                      title: 'Upgrade Settings',
                      keys: [
                        'upgradeTime',
                        'upgradeDamageMultiplier',
                        'upgradeHealthMultiplier',
                        'upgradeArmorAdditive',
                      ] as const,
                    },
                  ];

                  return groups.map((group, groupIndex) => {
                    const validKeys = group.keys.filter(
                      (key) =>
                        BUILDING_CONFIG_SCHEMA[key] &&
                        (BUILDING_CONFIG_SCHEMA[key].category === ConfigCategory.GAMEPLAY ||
                          BUILDING_CONFIG_SCHEMA[key].category === ConfigCategory.BUILDINGS)
                    );

                    if (validKeys.length === 0) return null;

                    const isExpanded = expandedSections[group.title] ?? false;

                    return (
                      <div
                        key={group.title}
                        className={`space-y-3 ${groupIndex > 0 ? 'pt-4 border-t border-gray-700' : ''}`}
                      >
                        <button
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [group.title]: !isExpanded,
                            }))
                          }
                          className="flex items-center gap-2 w-full text-left hover:opacity-80 transition-opacity"
                        >
                          <span
                            className={`text-yellow-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          >
                            ▶
                          </span>
                          <h4 className="text-lg font-semibold text-yellow-300">{group.title}</h4>
                        </button>
                        {isExpanded && (
                          <div className="space-y-3 pl-6">
                            {validKeys.map((key) => {
                              const metadata = BUILDING_CONFIG_SCHEMA[key];
                              if (!metadata) return null;

                              const settingValue = buildingSettings[key];
                              const numValue =
                                (settingValue as number | undefined) ??
                                DEFAULT_BUILDING_CONFIG[key];

                              return (
                                <div key={key} className="space-y-2">
                                  <label className="block text-gray-200">
                                    <span className="font-medium">{metadata.label}</span>
                                    {metadata.description && (
                                      <span className="block text-sm text-gray-400 mt-1">
                                        {metadata.description}
                                      </span>
                                    )}
                                  </label>
                                  <NumericSettingInput
                                    value={numValue}
                                    metadata={metadata}
                                    onChange={(val) => handleBuildingSettingChange(key, val)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={resetBuildingSettings}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reset Building Settings
                  </button>
                </div>
              </div>
            )}

            {activeCategory === ConfigCategory.UNITS && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-4">Unit Settings (Developer)</h3>

                {/* Define groups for unit settings */}
                {(() => {
                  const unitGroups = [
                    {
                      title: 'Builder Stats',
                      unitType: UnitType.BUILDER,
                      keys: [
                        'builderHealth',
                        'builderArmor',
                        'builderSightRange',
                        'builderAttackRange',
                        'builderBaseDamage',
                        'builderAttackSpeed',
                        'builderMovementSpeed',
                      ] as const,
                    },
                    {
                      title: 'Melee Stats',
                      unitType: UnitType.MELEE,
                      keys: [
                        'meleeHealth',
                        'meleeArmor',
                        'meleeSightRange',
                        'meleeAttackRange',
                        'meleeBaseDamage',
                        'meleeAttackSpeed',
                        'meleeMovementSpeed',
                      ] as const,
                    },
                    {
                      title: 'Ranged Stats',
                      unitType: UnitType.RANGED,
                      keys: [
                        'rangedHealth',
                        'rangedArmor',
                        'rangedSightRange',
                        'rangedAttackRange',
                        'rangedBaseDamage',
                        'rangedAttackSpeed',
                        'rangedMovementSpeed',
                      ] as const,
                    },
                    {
                      title: 'Enemy Melee Stats',
                      unitType: UnitType.ENEMY_MELEE,
                      keys: [
                        'enemyMeleeHealth',
                        'enemyMeleeArmor',
                        'enemyMeleeSightRange',
                        'enemyMeleeAttackRange',
                        'enemyMeleeBaseDamage',
                        'enemyMeleeAttackSpeed',
                        'enemyMeleeMovementSpeed',
                      ] as const,
                    },
                    {
                      title: 'Spawn Points',
                      unitType: null,
                      keys: ['playerSpawnX', 'playerSpawnY', 'enemySpawnX', 'enemySpawnY'] as const,
                    },
                  ];

                  return unitGroups.map((group, groupIndex) => {
                    const validKeys = group.keys.filter(
                      (key) =>
                        UNIT_CONFIG_SCHEMA[key] &&
                        UNIT_CONFIG_SCHEMA[key].category === ConfigCategory.UNITS
                    );

                    if (validKeys.length === 0) return null;

                    const isExpanded = expandedSections[group.title] ?? false;

                    return (
                      <div
                        key={group.title}
                        className={`space-y-3 ${groupIndex > 0 ? 'pt-4 border-t border-gray-700' : ''}`}
                      >
                        <button
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [group.title]: !isExpanded,
                            }))
                          }
                          className="flex items-center gap-2 w-full text-left hover:opacity-80 transition-opacity"
                        >
                          <span
                            className={`text-yellow-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          >
                            ▶
                          </span>
                          <h4 className="text-lg font-semibold text-yellow-300">{group.title}</h4>
                          {group.unitType && (
                            <span className="text-sm text-gray-400 ml-2">
                              (Default: {UNIT_DEFINITIONS[group.unitType].name})
                            </span>
                          )}
                        </button>
                        {isExpanded && (
                          <div className="space-y-3 pl-6">
                            {validKeys.map((key) => {
                              const metadata = UNIT_CONFIG_SCHEMA[key];
                              if (!metadata) return null;

                              // Get current value from dev settings, or fall back to default from UNIT_DEFINITIONS
                              let currentValue: number;
                              if (key.startsWith('playerSpawn') || key.startsWith('enemySpawn')) {
                                // Spawn points always use dev settings
                                currentValue =
                                  (unitSettings[key as keyof typeof unitSettings] as number) ??
                                  getDefaultUnitConfig()[
                                    key as keyof ReturnType<typeof getDefaultUnitConfig>
                                  ];
                              } else {
                                // Unit stats: use dev settings if set, otherwise use UNIT_DEFINITIONS
                                const devValue = unitSettings[key as keyof typeof unitSettings] as
                                  | number
                                  | undefined;
                                if (devValue !== undefined) {
                                  currentValue = devValue;
                                } else {
                                  // Get default from UNIT_DEFINITIONS
                                  const unitTypeMap: Record<string, UnitType> = {
                                    builder: UnitType.BUILDER,
                                    melee: UnitType.MELEE,
                                    ranged: UnitType.RANGED,
                                    enemyMelee: UnitType.ENEMY_MELEE,
                                  };
                                  const statNameMap: Record<
                                    string,
                                    keyof (typeof UNIT_DEFINITIONS)[UnitType.BUILDER]['stats']
                                  > = {
                                    Health: 'health',
                                    Armor: 'armor',
                                    SightRange: 'sightRange',
                                    AttackRange: 'attackRange',
                                    BaseDamage: 'baseDamage',
                                    AttackSpeed: 'attackSpeed',
                                    MovementSpeed: 'movementSpeed',
                                  };
                                  const match = key.match(
                                    /^(builder|melee|ranged|enemyMelee)(.+)$/
                                  );
                                  if (match) {
                                    const [, unitKey, statKey] = match;
                                    const unitType = unitTypeMap[unitKey];
                                    const statName = statNameMap[statKey];
                                    if (unitType && statName) {
                                      currentValue = UNIT_DEFINITIONS[unitType].stats[
                                        statName
                                      ] as number;
                                    } else {
                                      currentValue = 0;
                                    }
                                  } else {
                                    currentValue = 0;
                                  }
                                }
                              }

                              return (
                                <div key={key} className="space-y-2">
                                  <label className="block text-gray-200">
                                    <span className="font-medium">{metadata.label}</span>
                                    {metadata.description && (
                                      <span className="block text-sm text-gray-400 mt-1">
                                        {metadata.description}
                                      </span>
                                    )}
                                  </label>
                                  <NumericSettingInput
                                    value={currentValue}
                                    metadata={metadata}
                                    onChange={(val) => handleUnitSettingChange(key, val)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={resetUnitSettings}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reset Unit Settings
                  </button>
                </div>
              </div>
            )}

            {/* Future: Add other developer categories (Performance, etc.) */}
          </div>
        </div>
      </div>
    </div>
  );
}
